<template>
  <card-container
    :id="pageId"
    :loading-message="status.loadingMessage"
    :class="pageId"
    :is-loading="status.isLoading"
    :disabled="status.isLoading"
    :no-action="isNeedToCallUsToOrderFirstRide"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Schedule a Ride
    </template>

    <template #card-notes>
      <FirstRideOrderNotify v-if="isNeedToCallUsToOrderFirstRide" />
      <div
        v-else
        class="card-notes"
      >
        <p>
          <b>⚠️ NOTE:</b>
          All scheduled rides will start looking for a driver 10 mins before the scheduled time. This is because it takes some time for the
          car to get to the actual destination.
        </p>
      </div>
    </template>

    <template #content>
      <template v-if="isGeneralRideType">
        <div
          class="row"
          :class="{ 'align-items-center': !isPhoneNumberVisible }"
        >
          <label class="col-sm-3 control-label"> Preferred Contact Number </label>
          <div class="col-sm-8">
            <dropdown-list
              v-model="rideScheduler.riderPhone"
              :disabled="!isEditableForm"
              :options="contactPhoneNumbers"
            />
            <phone-number-input
              v-if="isPhoneNumberVisible"
              id="contact-phone"
              v-model="newPhoneNumber"
              class="mt-3"
              :page-id="pageId"
              :disabled="!isEditableForm"
            >
              <template #help-block>
                <span
                  v-if="status.preferredContactWarning"
                  class="error-message"
                >
                  {{ status.preferredContactWarning }}
                </span>
              </template>
            </phone-number-input>
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Trip Name* </label>
          <div class="col-sm-8">
            <sanitized-input
              v-model="rideScheduler.rideName"
              placeholder="Give a name to this trip so you can easily identify it."
              required
              :disabled="!isEditableForm"
            />
            <span class="help-block"> By adding a trip name, it help you identify the ride quicker in your ride history. </span>
          </div>
        </div>
      </template>

      <div
        v-if="availablePartnerRidePurposes.length > 0"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Ride Purpose* </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="rideScheduler.partnerRidePurpose"
            :disabled="!isEditableForm"
            :options="availablePartnerRidePurposes"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Type of Car* </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="rideScheduler.rideType"
            :disabled="!isEditableForm"
            :options="availableRideTypes"
            required
          />
        </div>
      </div>

      <stawav-ride-data
        v-if="isStawavRideType"
        :data="rideScheduler"
        :disabled-date="disabledDate"
        :is-disabled-fields="!isEditableForm"
        :is-show-time-warning-message="status.isShowTimeWarningMessage"
      />

      <local-taxi-ride-data-container
        v-if="isLocalTaxiRideType"
        :data="localTaxiRideData"
        :disabled-date="disabledDate"
      />

      <template v-if="isGeneralRideType">
        <ChooseScheduledRideType
          v-model="rideScheduler.scheduleKind"
          :scheduled-ride-id="scheduledRideId"
        />

        <repeat-ride-data
          v-if="rideScheduler.scheduleKind === 'multi'"
          :data="rideScheduler"
          :is-show-time-warning-message="status.isShowTimeWarningMessage"
          :disabled-date="disabledDate"
        />

        <single-ride-data
          v-if="rideScheduler.scheduleKind === 'single'"
          :data="rideScheduler"
          :is-show-time-warning-message="status.isShowTimeWarningMessage"
          :disabled-date="disabledDate"
          @change-pickup-time="checkPickupTimeIsNull"
        />

        <willcall-ride-data
          v-if="rideScheduler.scheduleKind === 'willcall'"
          :data="rideScheduler"
          :disabled-date="disabledDate"
        />

        <appointment-ride-data
          v-if="rideScheduler.scheduleKind === 'appointment'"
          :data="rideScheduler"
          :disabled-date="disabledDate"
          :is-show-time-warning-message="status.isShowTimeWarningMessage"
        />
      </template>

      <go-go-google-map
        map-type-id="roadmap"
        class="mt-10 mobile-mt-10"
        :markers="[markers[0]]"
        :map-center="getMapCenter(0)"
        :is-draggable="isEditableForm"
        :zoom="18"
        collapse-title="Pickup/Start Location"
        @change="setMarkerPosition"
      />

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Pickup/Start Location </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="pickupLocationId"
            :disabled="!isEditableForm"
            :options="addressList"
          />
        </div>
      </div>

      <expand-address-section
        v-if="isStartAddressFormVisible"
        v-model="startAddress"
        show-location-name
        :parent-id="pageId"
        :is-editing="isEditableForm"
        :show-geocode="false"
        :show-home-note="false"
        :show-gourmet-note="false"
        :show-map="false"
        :show-apt="false"
        :show-google-place-id="false"
        :anchor-bound="endAddressAnchorBound"
      />

      <go-go-google-map
        map-type-id="roadmap"
        class="mt-30 mobile-mt-10"
        :markers="[markers[1]]"
        :map-center="getMapCenter(1)"
        :is-draggable="isEditableForm"
        :zoom="18"
        collapse-title="DropOff/End Location"
        @change="setMarkerPosition"
      />

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> DropOff/End Location </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="dropoffLocationId"
            :disabled="!isEditableForm"
            :options="addressList"
          />
        </div>
      </div>

      <expand-address-section
        v-if="isEndAddressFormVisible"
        v-model="endAddress"
        show-location-name
        :parent-id="pageId"
        :is-editing="isEditableForm"
        :show-geocode="false"
        :show-home-note="false"
        :show-gourmet-note="false"
        :show-map="false"
        :show-apt="false"
        :show-google-place-id="false"
        :anchor-bound="startAddressAnchorBound"
      />

      <div class="row">
        <label class="col-sm-3 control-label pt-5"> Note to Driver </label>
        <div class="col-sm-8">
          <sanitized-textarea
            id="noteToDriver"
            v-model="rideScheduler.noteToDriver"
            autosize
            :disabled="!isEditableForm"
          />
          <span class="help-block"> This note will be sent to the driver. </span>
        </div>
      </div>

      <template v-if="isGeneralRideType">
        <template v-if="isPartnerDashboard">
          <div class="row">
            <div class="col-sm-5 offset-sm-3">
              <div>
                <el-checkbox
                  id="isExpandDriverSearch"
                  v-model="rideScheduler.isExpandDriverSearch"
                >
                  Expand Driver Search
                </el-checkbox>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-5 offset-sm-3">
              <div>
                <el-checkbox
                  id="isExpandVehicleSearch"
                  v-model="rideScheduler.isExpandVehicleSearch"
                >
                  Expand Vehicle Search
                </el-checkbox>
              </div>
            </div>
          </div>
        </template>

        <template v-if="isShowConfirmSurge">
          <div class="row mb-0">
            <label class="col-sm-3 control-label pt-5"> Confirm Surge* </label>
            <div class="col-sm-8">
              <el-radio
                v-model="rideScheduler.callToConfirmSurge"
                label="yes"
              >
                Call to confirm surge
              </el-radio>

              <el-radio
                v-model="rideScheduler.callToConfirmSurge"
                label="no"
              >
                Don’t call to confirm surge
              </el-radio>
            </div>
          </div>

          <div class="row">
            <div class="offset-sm-3 col-sm-8 col-12">
              If a scheduled ride has surge pricing (higher price than usual) then we will call you to confirm the price before the car gets
              sent out. However, if "Call to confirm surge" checkbox is not checked then a car will get sent automatically no matter what
              the price is.
            </div>
          </div>
        </template>

        <div
          v-if="isPartnerDashboard"
          class="row"
        >
          <label class="col-sm-3 control-label" />
          <div class="col-sm-7">
            <el-checkbox
              id="isDoNotApplySubsidy"
              v-model="rideScheduler.isDoNotApplySubsidy"
              class="pb-0 font-weight-bold"
            >
              Do not apply partner subsidy to this ride(s)
            </el-checkbox>
            <div
              class="help-block"
              style="padding: 0 0 0 35px"
            >
              This option should only be selected if the customer wants to pay for the full amount of the ride and does not want it to count
              towards their subsidized rides.
            </div>
          </div>
        </div>
      </template>

      <message-html
        :result-html="status.resultHtml"
        :is-top-line="true"
      />

      <template v-if="similarScheduledRide">
        <hr>
        <similar-scheduled-ride-viewer :scheduled-ride="similarScheduledRide" />
      </template>

      <template v-if="status.activeStep === ScheduleRideStep.ScheduleRide">
        <hr>
        <stawav-estimation-viewer
          v-if="isStawavRideType"
          :estimation="stawavEstimation"
          :is-one-way-trip="!rideScheduler.returnTime"
        />
        <template v-else-if="!!estimationRide">
          <PartnerEstimateTabs
            :tabs="partnerEstimateTabs"
            :selected-index="selectedPartnerEstimateIndex"
            :all-partner-estimates="allPartnerEstimates"
            :selected-partner-estimate="selectedPartnerEstimate"
            @select="onSelectPartnerEstimateTab"
          />
          <estimation-ride-viewer
            v-model:operatorConfirmedSurgePriceWithCustomer="estimationRide.operatorConfirmedSurgePriceWithCustomer"
            :estimation-ride="estimationRide"
            :show-full-detail="!isHideRideEstimateFullDetail"
            :preferred-contact-warning="status.preferredContactWarning"
            is-scheduled-ride
            :call-to-confirm-surge="rideScheduler.callToConfirmSurge"
            @update:call-to-confirm-surge="rideScheduler.callToConfirmSurge = $event"
          />
        </template>
      </template>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import Moment from 'moment-timezone';
import * as _ from 'lodash';

import util from '@/Utils/util';
import { sendPost, sendDelete } from '@/Utils/api';
import rideUtility from '@/Utils/rideUtility';
import { defaultTimeZone } from '@/constants/app';

import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { usePartner } from '@/use/partner';
import { useRideTypes } from '@/use/rideTypes';
import { useScheduledRide } from '@/use/scheduledRide';
import { useAddress } from '@/use/address';
import { useRide } from '@/use/ride';
import { useSwalDialog } from '@/use/swalDialog';
import { useReturnTripAlert } from '@/use/returnTripAlert';
import { usePartnerEstimates } from '@/use/partnerEstimates';
import { CustomLocationType } from '@/models/address';
import { MedicalTripResult, RideServiceType } from '@/models/ride';
import { ScheduledRide } from '@/models/scheduledRide';
import { scheduledRideService } from '@/services/scheduledRide';
import { callerService } from '@/services/caller';
import { rideService } from '@/services/ride';

const LocalTaxiRideDataContainer = defineAsyncComponent(
  () => import('@/components/Main/Operator/ScheduleRide/LocalTaxiRideData.vue') as any,
);

const StawavRideData = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/StawavRideData.vue') as any);

const StawavEstimationViewer = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/StawavEstimation.vue') as any);

const RepeatRideData = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/RepeatRideData.vue') as any);

const SingleRideData = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/SingleRideData.vue') as any);

const WillcallRideData = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/WillcallRideData.vue') as any);

const AppointmentRideData = defineAsyncComponent(() => import('@/components/Main/Operator/ScheduleRide/AppointmentRideData.vue') as any);

const EstimationRideViewer = defineAsyncComponent(() => import('@/components/UIComponents/OrderingForms/EstimationRide.vue') as any);

const SimilarScheduledRideViewer = defineAsyncComponent(
  () => import('@/components/UIComponents/OrderingForms/SimilarScheduledRide.vue') as any,
);

const ChooseScheduledRideType = defineAsyncComponent(
  () => import('@/components/Main/Operator/ScheduleRide/ChooseScheduledRideType.vue') as any,
);

const FirstRideOrderNotify = defineAsyncComponent(() => import('./FirstRideOrderNotify.vue') as any);

const PartnerEstimateTabs = defineAsyncComponent(
  () => import('@/components/UIComponents/OrderingForms/PartnerEstimateTabs.vue') as any,
);

const pageId = 'family-scheduled-ride';
const similarScheduledRideLimitDurationMins = 20;
const disallowedPlaceTypesToPickUp = ['airport', 'bus_station', 'train_station'];

enum ScheduleRideStep {
  GettingEstimation = 0,
  ScheduleRide = 1,
}

const props = withDefaults(
  defineProps<{
    hasCompletedRide?: boolean;
  }>(),
  {
    hasCompletedRide: true,
  },
);

const route = useRoute();

const { isFamilyDashboard, isPartnerDashboard } = useDashboard();

const {
  customerInfo,
  rideAvailablePhoneNumber,
  contactPhoneNumbers,
  addressList,
  customerPartner,
  customerPartnerCode,
  customerPartnerId,
  availablePartnerRidePurposes,
  isHideRideEstimateFullDetail,
} = useCustomer();

const { partnerId, partnerCode, partnerInfo } = usePartner();
const { getScheduledRideTypes, findPartnerLocalTaxiRideType, getAvailableRideType } = useRideTypes();

const { updateAddressWithGeocode, trimAddress, getFormattedAddress, getAnchorBound } = useAddress();

const {
  duplicatedScheduledRide,
  setDuplicatedScheduledRide,
  checkMedicalTripForDiamondBarPartnership,
  checkMedicalTripForRotcPartnership,
} = useRide();

const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog, showWarningPopupDialog } = useSwalDialog();
const { showReturnTripAlertIfNeeded } = useReturnTripAlert();

const rideScheduler: Ref<any> = ref({
  riderPhone: rideAvailablePhoneNumber.value,
  rideName: '',
  rideType: '',
  partnerRidePurpose: '',
  serviceType: RideServiceType.Transportation,
  rideMultiple: false,
  scheduleKind: 'single',
  pickupDate: null,
  pickupTime: null,
  scheduledAppointmentDate: null,
  scheduledAppointmentTime: null,
  scheduledAppointmentBufferMinutes: 10,
  repeatEveryXWeek: 1,
  day0: false,
  day1: false,
  day2: false,
  day3: false,
  day4: false,
  day5: false,
  day6: false,
  endOn: null,
  endOnType: 'never',
  endAfterXTimes: 0,
  noteToDriver: '',
  callToConfirmSurge: 'yes',
  minutesAfterCreationCustomReminder: customerPartner?.value?.callOptions?.scheduledRideReminderMinutesAfterCreation || '',
  overrideBadDriver: false,
  overrideBigCar: false,
  overrideSmallCar: false,
  overrideBannedCar: false,
  isExpandDriverSearch: false,
  isExpandVehicleSearch: false,
  isDoNotApplySubsidy: false,
  isMedicalTrip: false,
});
const status = reactive({
  isLoading: false,
  loadingMessage: '',
  preferredContactWarning: '',
  isShowTimeWarningMessage: false,
  resultHtml: '',
  activeStep: ScheduleRideStep.GettingEstimation,
  isShowNormalRideEstimation: false,
});
const scheduledRideId = ref(0);
const newPhoneNumber = ref('');
const pickupLocationId = ref('home');
const dropoffLocationId = ref('home');

const startAddress: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress1: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
  googlePlaceTypes: [],
});
const endAddress: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress1: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
  googlePlaceTypes: [],
  type: false,
});

const markers: Ref<any> = ref([
  {
    position: {
      lat: 0.0,
      lng: 0.0,
    },
    initialPosition: {
      lat: 0.0,
      lng: 0.0,
    },
    index: 0,
    isDraggable: true,
    isShow: false,
    icon: '/static/img/pickup_marker.png',
  },
  {
    position: {
      lat: 0.0,
      lng: 0.0,
    },
    initialPosition: {
      lat: 0.0,
      lng: 0.0,
    },
    index: 1,
    isDraggable: true,
    isShow: false,
    icon: '/static/img/dropoff_marker.png',
  },
]);

const estimationRide: Ref<any> = ref(null);
const {
  allPartnerEstimates,
  selectedPartnerEstimateIndex,
  partnerEstimateTabs,
  selectedPartnerEstimate,
  setPartnerEstimatesFromResponse,
  resetPartnerEstimates,
  selectPartnerEstimateTab,
  getOrderingPartnerId,
} = usePartnerEstimates({
  onClear: () => {
    if (estimationRide.value) {
      estimationRide.value.operatorConfirmedSurgePriceWithCustomer = false;
    }
  },
});
const similarScheduledRide: Ref<ScheduledRide | null> = ref(null);
const localTaxiRideData = ref({
  pickupDate: '',
  pickupTime: '',
  noteToTaxiCompany: '',
  returnTime: '',
});
const stawavEstimation = ref({
  wheelchairTransportationRate: 75,
  wheelchairTransportationStandardRate: 75,
  wheelchairTransportationSurchargeRate: 0,
  passengerTransportationRate: 60,
  passengerTransportationStandardRate: 60,
  passengerTransportationSurchargeRate: 0,
  waitTimeRate: 0,
});

const {
  isLocalTaxiRideType,
  isGeneralRideType,
  isStawavRideType,
  estimationBtnText,

  getSchedulePostData,
  getStawavRideData,
} = useScheduledRide(rideScheduler);

const preferredVendorRideTypes = computed(() => customerPartner.value?.preferredVendorRideTypes || []);
const availableRideTypes = computed(() =>
  getScheduledRideTypes(customerPartnerCode.value || partnerCode.value, preferredVendorRideTypes.value),
);
const isNeedToCallUsToOrderFirstRide = computed(() => isFamilyDashboard.value && !props.hasCompletedRide);

const isEditableForm = computed(() => {
  if (isNeedToCallUsToOrderFirstRide.value) {
    return false;
  }

  return status.activeStep === ScheduleRideStep.GettingEstimation
});
const isPhoneNumberVisible = computed(() => !rideScheduler.value.riderPhone);
const isStartAddressFormVisible = computed(() => pickupLocationId.value === '');
const isEndAddressFormVisible = computed(() => dropoffLocationId.value === '');

const isRiderPhoneNumberValid = computed(() => util.isPhoneNumberValid(rideScheduler.value.riderPhone || newPhoneNumber.value));

const submitBtnText = computed(() => {
  if (status.activeStep === ScheduleRideStep.GettingEstimation) {
    return estimationBtnText.value;
  }

  return scheduledRideId.value > 0 ? 'Update' : 'Create';
});

const startAddressAnchorBound = computed(() => getAnchorBound(startAddress.value));
const endAddressAnchorBound = computed(() => getAnchorBound(endAddress.value));

const isShowConfirmSurge = computed(() => {
  if (!isPartnerDashboard.value) {
    return false;
  }

  return !partnerInfo.value?.isAutoConfirmSurge;
});

const disabledDate = (date: Date) => {
  const dateLocalMoment = Moment(date);
  const startOfTodayDefaultTimeZoneMoment = Moment.tz(customerInfo.value.timeZone || defaultTimeZone).startOf('day');
  const startOfTodayLocalMoment = Moment(startOfTodayDefaultTimeZoneMoment.format('YYYY-MM-DD hh:mm'));
  return dateLocalMoment.isBefore(startOfTodayLocalMoment, 'day');
};

const getMapCenter = (index: number) => {
  return {
    lat: markers.value[index]?.position?.lat || 37.42248,
    lng: markers.value[index]?.position?.lng || -122.08547,
  };
};

const setMarkerPosition = (
  lat: number | string | null,
  lng: number | string | null,
  index: number,
  isMarkerChanged: boolean = true,
  isAddressChanged: boolean = false,
) => {
  util.updateMarkerPosition(markers.value[index], lat, lng, isAddressChanged);

  if (!isMarkerChanged) {
    return;
  }

  if (index === 0) {
    startAddress.value = updateAddressWithGeocode(startAddress.value, lat, lng);
  } else if (index === 1) {
    endAddress.value = updateAddressWithGeocode(endAddress.value, lat, lng);
  }
};

const getRideScheduler = async () => {
  /*
   locationIds has 'home' as its default value, but we need to set it to an empty value to prevent the watch hook from being triggered unnecessarily.
   In the code below, we check whether the current address exists in the list of custom locations.
   If the address is found in the custom locations, we set the corresponding locationId so the correct address is selected from the dropdown.
   However, if the address is not found, locationId will be an empty string. This causes the watch hook to trigger and reset the address, which prevents us from keeping the original address.
  */
  pickupLocationId.value = '';
  dropoffLocationId.value = '';
  status.isLoading = true;
  status.loadingMessage = 'Loading Scheduled Ride...';

  try {
    const response = await scheduledRideService.fetchScheduledRideRecords({
      scheduledRideId: scheduledRideId.value,
    });

    if (response.count > 0) {
      const data = response.rows[0];
      const data1 = rideUtility.getRideSchedulerData(data);

      startAddress.value = _.cloneDeep(data1.startAddress);
      endAddress.value = _.cloneDeep(data1.endAddress);
      pickupLocationId.value = getCustomLocationId(startAddress.value);
      dropoffLocationId.value = getCustomLocationId(endAddress.value);
      rideScheduler.value = _.omit(data1, ['startAddress', 'endAddress']);
    }
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
  status.isLoading = false;
};

const getCustomLocationId = (address: CustomLocationType) => {
  const customLocationItem = addressList.value.find((item) => {
    if (!item.address) {
      return false;
    }

    return (
      item.address.streetAddress === address.streetAddress &&
      item.address.city === address.city &&
      item.address.state === address.state &&
      item.address.zip === address.zip
    );
  });

  return customLocationItem?.key || '';
};

const checkMedicalTripForPartnerships = async (isLocalTaxiRideType: boolean = false) => {
  rideScheduler.value.isMedicalTrip = false;

  const resultDiamondBarPartnership = await checkMedicalTripForDiamondBarPartnership(startAddress.value, endAddress.value);
  const resultRotcPartnership = await checkMedicalTripForRotcPartnership(startAddress.value, endAddress.value);

  const results = [resultDiamondBarPartnership, resultRotcPartnership];

  const isValidationFailed = results.some((result) => result === MedicalTripResult.VALIDATION_FAILED);
  const isUserDeclined = results.some((result) => result === MedicalTripResult.USER_DECLINED);
  const isValidationPassed = results.some((result) => result === MedicalTripResult.VALIDATION_PASSED);

  if (isLocalTaxiRideType) {
    if (isValidationFailed || isUserDeclined) {
      return false;
    }

    if (isValidationPassed) {
      rideScheduler.value.isMedicalTrip = true;
    }
  } else {
    if (isValidationFailed) {
      return false;
    }

    if (isUserDeclined) {
      rideScheduler.value.isDoNotApplySubsidy = true;
    }

    if (isValidationPassed) {
      rideScheduler.value.isMedicalTrip = true;
    }
  }

  return true;
};

const checkPreferredNumber = async () => {
  status.preferredContactWarning = '';

  if (!isPhoneNumberVisible.value) {
    // if user choose the phone number from the dropdown, we don't need to check preferred contact number
    return;
  }

  if (!isRiderPhoneNumberValid.value) {
    return;
  }

  try {
    const response = await callerService.getCallerLovedOneIdName(newPhoneNumber.value);

    status.preferredContactWarning = util.checkAndGetBelongsWarningMessage(customerInfo.value, response, {
      isFamilyDashboard: isFamilyDashboard.value,
      partnerId: partnerId.value,
    });

    if (response) {
      const text = isFamilyDashboard.value
        ? 'Please make sure this is the phone number your loved one can use to talk to their driver!'
        : 'Please make sure this is the phone number the user wants for this ride!';

      showWarningPopupDialog({
        text,
        title: status.preferredContactWarning,
      });
    }
  } catch (error) {
    console.log(error);
  }
};

const getActiveRide = async () => {
  let activeRide = null;
  try {
    activeRide = await rideService.getActiveRide(customerInfo.value.id);
  } catch (error) {
    console.log(error);
  }

  return activeRide;
};

const cancelActiveRide = async (requestId: string) => {
  const data = {
    requestId,
    opCancellationReason: 'Rider wants to schedule a ride instead',
  };

  try {
    await sendDelete('rides', {
      data,
    });

    showSuccessPopupDialog({
      text: 'The active ride has been cancelled successfully!',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const checkActiveRide = async () => {
  const activeRide = await getActiveRide();

  if (!activeRide) {
    return;
  }

  const isConfirmedToCancelActiveRide = await showConfirmPopupDialog({
    text: 'The rider has an active ride would you like to cancel this ride?',
    title: 'Confirm with Client?',
    confirmButtonText: 'Yes, cancel',
  });

  if (!isConfirmedToCancelActiveRide) {
    return;
  }

  await cancelActiveRide(activeRide.requestId!);
};

const checkSimilarRide = async () => {
  let currentRideDateTime = util.formatDateTime(rideScheduler.value.pickupDate, 'YYYY-MM-DD');
  currentRideDateTime = `${currentRideDateTime} ${util.formatDateTime(rideScheduler.value.pickupTime, 'hh:mm:ss A')}`;
  similarScheduledRide.value = null;

  try {
    const response = await scheduledRideService.fetchScheduledRideRecords({
      phoneNumber: rideAvailablePhoneNumber.value,
    });

    for (const scheduledRide of response.rows) {
      if (scheduledRideId.value && scheduledRideId.value === scheduledRide.id) {
        continue;
      }

      if (!scheduledRide.completedAt && !scheduledRide.canceledAt && !similarScheduledRide.value) {
        const pickupDateTime = util.formatDateTime(scheduledRide.firstPickup, 'YYYY-MM-DD HH:mm:ss');
        const durationMins = Math.abs(Moment.duration(Moment(currentRideDateTime).diff(Moment(pickupDateTime))).asMinutes());

        if (durationMins < similarScheduledRideLimitDurationMins) {
          similarScheduledRide.value = _.cloneDeep(scheduledRide);
          break;
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
};

const showPopupToChangePickupTime = async () => {
  const newPickupTime = Moment(rideScheduler.value.pickupTime).subtract(estimationRide.value.eta, 'minutes').toDate();
  const formattedTime = util.formatDateTime(newPickupTime, 'hh:mm A');
  const questionText = `
    It looks drivers in the area are far away from this scheduled ride's pick up location.
    Please ask the client if they would like to schedule their pick up at ${formattedTime} to give the driver more time to get to them.
  `;

  const isChangePickupTimeConfirmed = await showConfirmPopupDialog({
    text: questionText,
    title: 'Confirm with Client',
    confirmButtonText: 'Confirmed',
  });

  if (!isChangePickupTimeConfirmed) {
    return;
  }

  rideScheduler.value.pickupTime = newPickupTime;
};

const getRideDateLocal = () => {
  const dispatchBuffer = 10; // The scheduled ride is dipatched 10 mins beforehand
  if (rideScheduler.value.scheduleKind === 'appointment') {
    const guessedRideDuration = 15; // assume the ride takes 15 minutes for better adjustement to traffic
    const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = rideScheduler.value;
    const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + guessedRideDuration + dispatchBuffer;

    const pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes').format('hh:mm A');
    return `${util.formatDateTime(scheduledAppointmentDate, 'YYYY-MM-DD')} ${pickupTime}`;
  }

  const pickupTime = Moment(rideScheduler.value.pickupTime).subtract(dispatchBuffer, 'minutes').format('hh:mm A');

  if (scheduledRideId.value && rideScheduler.value.rideMultiple && rideScheduler.value.nextPickupDate) {
    return `${util.formatDateTime(rideScheduler.value.nextPickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
  }

  return `${util.formatDateTime(rideScheduler.value.pickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
};

const getEstimationPostData = () => {
  trimAddress(startAddress.value);
  trimAddress(endAddress.value);

  const postData: any = rideUtility.makeEstimationPostData(
    {
      ...rideScheduler.value,
      typeOfCar: rideScheduler.value.rideType,
      startAddress: startAddress.value,
      endAddress: endAddress.value,
      rideDateLocal: getRideDateLocal(),
      phoneNumber: rideAvailablePhoneNumber.value,
    },
    startAddress.value.streetAddress,
    endAddress.value.streetAddress,
    false,
  );

  if (markers.value[1].isShow) {
    postData.endCoors = {
      lat: endAddress.value.latitude,
      lng: endAddress.value.longitude,
    };
  }

  if (rideScheduler.value.scheduleKind === 'willcall') {
    postData.isWillCall = true;
  }

  postData.callerId = customerInfo.value?.id;
  postData.overrideActiveRideCheck = true;

  if (isPartnerDashboard.value) {
    postData.partnerId = partnerId.value;
    postData.isDoNotApplySubsidy = rideScheduler.value.isDoNotApplySubsidy ? 1 : 0;
  }

  return postData;
};

const applyEstimationData = async (estimationData: any, { showWarnings = true }: { showWarnings?: boolean } = {}) => {
  estimationRide.value = rideUtility.parseEstimationRide({
    estimationData,
  });

  if (rideScheduler.value.scheduleKind === 'appointment') {
    const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = rideScheduler.value;
    const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + estimationRide.value.duration;
    rideScheduler.value.pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes');
    rideScheduler.value.pickupDate = scheduledAppointmentDate;
  }

  if (!showWarnings) {
    if (estimationRide.value.warningMessage) {
      showWarningPopupDialog({
        text: estimationRide.value.warningMessage,
      });
    }
    return;
  }

  await showReturnTripAlertIfNeeded(estimationRide.value.isNoReturnTrip, estimationRide.value.returnTripEtaMins);

  if (estimationData.status === 200 && estimationData.eta >= 15) {
    showPopupToChangePickupTime();
  }
};

const onSelectPartnerEstimateTab = (index: number) => selectPartnerEstimateTab(index, applyEstimationData);

const getWebEstimate = async () => {
  const postData = getEstimationPostData();

  status.resultHtml = '';

  try {
    const response = await sendPost('getWebEstimate', postData);
    const estimationData = setPartnerEstimatesFromResponse(response.data);

    await applyEstimationData(estimationData);

    status.activeStep = ScheduleRideStep.ScheduleRide;
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
};

const createUpdateScheduleRide = async () => {
  const orderingPartnerId = getOrderingPartnerId(partnerId.value || customerPartnerId.value);
  const postData = getSchedulePostData({
    phoneNumber: rideAvailablePhoneNumber.value,
    startAddress: startAddress.value,
    endAddress: endAddress.value,
    estimationRide: estimationRide.value,
    partnerId: orderingPartnerId,
  });

  if (!postData.riderPhone) {
    postData.riderPhone = newPhoneNumber.value;
  }

  postData.callToConfirmSurge = rideScheduler.value.callToConfirmSurge === 'yes' ? 1 : 0;

  if (isPartnerDashboard.value) {
    postData.overrideBigCar = rideScheduler.value.overrideBigCar ? Number(rideScheduler.value.overrideBigCar) : 0;
    postData.overrideSmallCar = rideScheduler.value.overrideSmallCar ? Number(rideScheduler.value.overrideSmallCar) : 0;
    postData.overrideBannedCar = rideScheduler.value.overrideBannedCar ? Number(rideScheduler.value.overrideBannedCar) : 0;
    postData.overrideBadDriver = rideScheduler.value.overrideBadDriver ? Number(rideScheduler.value.overrideBadDriver) : 0;
    postData.isDoNotApplySubsidy = rideScheduler.value.isDoNotApplySubsidy ? Number(rideScheduler.value.isDoNotApplySubsidy) : 0;
    postData.minutesAfterCreationCustomReminder =
      typeof rideScheduler.value.minutesAfterCreationCustomReminder === 'number'
        ? rideScheduler.value.minutesAfterCreationCustomReminder
        : null;
  }

  status.isLoading = true;
  status.loadingMessage = scheduledRideId.value ? 'Updating Scheduled Ride...' : 'Creating Scheduled Ride...';

  try {
    if (!scheduledRideId.value) {
      const isWillCall = rideScheduler.value.scheduleKind === 'willcall';
      const disallowVendorScheduledRide = rideScheduler.value.scheduleKind === 'appointment' || rideScheduler.value.serviceType !== RideServiceType.Transportation;
      const response = isWillCall
        ? await scheduledRideService.createWillCallRide(postData)
        : await scheduledRideService.createScheduledRide(postData, disallowVendorScheduledRide);

      await showSuccessPopupDialog({
        text: `Your ${isWillCall ? '"Will Call" ' : 'scheduled '}ride has been created. Confirmation number: ${response.id}`,
      });
    } else {
      await scheduledRideService.updateScheduledRide({
        scheduledRideId: scheduledRideId.value,
        data: postData,
      });

      await showSuccessPopupDialog({
        text: `Your scheduled ride has been updated. Confirmation number: ${scheduledRideId.value}`,
      });
    }

    resetForm();
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }

  status.isLoading = false;
};

const createLocalTaxiScheduledRide = async () => {
  status.isLoading = true;
  status.resultHtml = '';

  try {
    await scheduledRideService.createLocalTaxiScheduledRide({
      phoneNumber: rideAvailablePhoneNumber.value,
      pickupDate: util.formatDateTime(localTaxiRideData.value.pickupDate),
      pickupTime: util.formatDateTime(localTaxiRideData.value.pickupTime, 'hh:mm A'),
      returnTime: localTaxiRideData.value.returnTime ? util.formatDateTime(localTaxiRideData.value.returnTime, 'hh:mm A') : null,
      pickupAddress: getFormattedAddress(startAddress.value),
      dropoffAddress: getFormattedAddress(endAddress.value),
      rideType: rideScheduler.value.rideType,
      partnerRidePurpose: rideScheduler.value.partnerRidePurpose,
      notes: localTaxiRideData.value.noteToTaxiCompany,
      isMedicalTrip: rideScheduler.value.isMedicalTrip,
    });

    showSuccessPopupDialog({
      text: 'The local taxi scheduled ride has been created successfully!',
    });

    resetForm();
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const getStawavEstimation = async () => {
  const payload = getStawavRideData({
    phoneNumber: rideAvailablePhoneNumber.value,
    startAddress: startAddress.value,
    endAddress: endAddress.value,
    partnerId: partnerId.value || customerPartnerId.value,
  });

  status.isLoading = true;
  status.loadingMessage = 'Getting Stawav Scheduled Ride Estimation...';
  status.resultHtml = '';
  try {
    const response = await scheduledRideService.getStaWavScheduledRideEstimation(payload);

    stawavEstimation.value.wheelchairTransportationRate = response.wheelchairTransportationRate;
    stawavEstimation.value.wheelchairTransportationStandardRate = response.wheelchairTransportationStandardRate;
    stawavEstimation.value.wheelchairTransportationSurchargeRate = response.wheelchairTransportationSurchargeRate;
    stawavEstimation.value.passengerTransportationRate = response.passengerTransportationRate;
    stawavEstimation.value.passengerTransportationStandardRate = response.passengerTransportationStandardRate;
    stawavEstimation.value.passengerTransportationSurchargeRate = response.passengerTransportationSurchargeRate;
    stawavEstimation.value.waitTimeRate = response.waitTimeRate;

    status.activeStep = ScheduleRideStep.ScheduleRide;
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const createOrUpdateStawavScheduledRide = async () => {
  const payload = getStawavRideData({
    phoneNumber: rideAvailablePhoneNumber.value,
    startAddress: startAddress.value,
    endAddress: endAddress.value,
    partnerId: partnerId.value || customerPartnerId.value,
  });

  status.isLoading = true;
  status.resultHtml = '';

  try {
    let response: ScheduledRide;
    if (scheduledRideId.value) {
      response = await scheduledRideService.replaceStaWavScheduledRide({
        data: payload,
        scheduledRideId: scheduledRideId.value,
      });
    } else {
      response = await scheduledRideService.createStaWavScheduledRide({
        data: payload,
      })
    }

    showSuccessPopupDialog({
      text: `Your scheduled ride has been ${scheduledRideId.value ? 'updated' : 'created'}. Confirmation number: ${response.id}`,
    });

    resetForm();
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const submitForm = async () => {
  if (isLocalTaxiRideType.value) {
    const partnerLocalTaxiRideType = findPartnerLocalTaxiRideType(customerPartnerCode.value, rideScheduler.value.rideType);

    if (partnerLocalTaxiRideType?.data?.partnerTaxiVendor?.applySubsidyCondition) {
      const resultMedicalTrip = await checkMedicalTripForPartnerships(true);
      if (!resultMedicalTrip) {
        return;
      }
    }

    return createLocalTaxiScheduledRide();
  }

  if (status.activeStep === ScheduleRideStep.GettingEstimation) {
    if (isStawavRideType.value) {
      return getStawavEstimation();
    }

    status.isLoading = true;
    status.loadingMessage = 'Getting Estimation...';

    const resultMedicalTrip = await checkMedicalTripForPartnerships();
    if (!resultMedicalTrip) {
      status.isLoading = false;
      return;
    }

    await checkPreferredNumber();
    await checkActiveRide();
    await checkSimilarRide();
    await getWebEstimate();
    status.isLoading = false;
    return;
  }

  if (isStawavRideType.value) {
    return createOrUpdateStawavScheduledRide();
  }

  createUpdateScheduleRide();
};

const resetForm = () => {
  status.activeStep = ScheduleRideStep.GettingEstimation;
  status.resultHtml = '';
  status.preferredContactWarning = '';
  status.isShowTimeWarningMessage = false;

  rideScheduler.value = {
    riderPhone: rideAvailablePhoneNumber.value,
    rideName: '',
    rideType: getAvailableRideType({
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    }),
    partnerRidePurpose: '',
    serviceType: RideServiceType.Transportation,
    rideMultiple: false,
    scheduleKind: 'single',
    pickupDate: null,
    pickupTime: null,
    scheduledAppointmentDate: null,
    scheduledAppointmentTime: null,
    scheduledAppointmentBufferMinutes: 10,
    repeatEveryXWeek: 1,
    day0: false,
    day1: false,
    day2: false,
    day3: false,
    day4: false,
    day5: false,
    day6: false,
    endOn: null,
    endOnType: 'never',
    endAfterXTimes: 0,
    noteToDriver: '',
    callToConfirmSurge: 'yes',
    minutesAfterCreationCustomReminder: customerPartner?.value?.callOptions?.scheduledRideReminderMinutesAfterCreation || '',
    isDoNotApplySubsidy: false,
    isMedicalTrip: false,
  };

  localTaxiRideData.value = {
    pickupDate: '',
    pickupTime: '',
    noteToTaxiCompany: '',
    returnTime: '',
  };

  estimationRide.value = null;
  similarScheduledRide.value = null;
  pickupLocationId.value = 'home';
  dropoffLocationId.value = 'home';
  newPhoneNumber.value = '';
  resetPartnerEstimates();

  scheduledRideId.value = 0;
};

const checkPickupLocationType = () => {
  if (isPartnerDashboard.value) {
    return;
  }

  if (_.intersection(startAddress.value.googlePlaceTypes || [], disallowedPlaceTypesToPickUp).length === 0) {
    return;
  }

  const text = `
    We determined that the pickup location you input is one of the following (airport, bus terminal or a train station).<br>
    Please give us a call at 855-464-6872 once you have arrived and collected your luggage
    so our operator can assist you in ordering the ride. We do not allow scheduled pick ups from these locations as there can be delays and
    we want to avoid our clients getting a cancellation fee.
  `;
  showWarningPopupDialog({
    text,
    title: 'Call Us',
    type: 'html',
  });

  startAddress.value = {
    streetAddress: '',
    streetAddress1: '',
    city: '',
    state: '',
    zip: '',
    name: '',
    latitude: null,
    longitude: null,
    googlePlaceId: null,
    googlePlaceTypes: [],
  };
};

const checkPickupTimeIsNull = (data: string | null) => {
  if (!data) {
    rideScheduler.value.pickupTime = null;
    status.isShowTimeWarningMessage = false;
  }
};

watch(
  pickupLocationId,
  async (value: string) => {
    const addressItem = addressList.value.find((item: any) => item.key === value);
    startAddress.value = {
      streetAddress: addressItem?.address?.streetAddress || '',
      streetAddress1: '',
      city: addressItem?.address?.city || '',
      state: addressItem?.address?.state || '',
      zip: addressItem?.address?.zip || '',
      name: addressItem?.address?.name || '',
      latitude: addressItem?.address?.latitude ? Number(addressItem?.address?.latitude) : null,
      longitude: addressItem?.address?.longitude ? Number(addressItem?.address?.longitude) : null,
      googlePlaceId: addressItem?.address?.googlePlaceId,
      googlePlaceTypes: [],
    };
  },
  {
    immediate: true,
  },
);

watch(
  startAddress,
  (value: any, oldValue: any) => {
    const isAddressChanged = util.isAddressChanged(value, oldValue);

    const { latitude, longitude } = value;

    setMarkerPosition(Number(latitude), Number(longitude), 0, false, isAddressChanged);

    checkPickupLocationType();
  },
  {
    immediate: true,
  },
);

watch(
  dropoffLocationId,
  async (value: string) => {
    const addressItem = addressList.value.find((item: any) => item.key === value);
    endAddress.value = {
      streetAddress: addressItem?.address?.streetAddress || '',
      streetAddress1: '',
      city: addressItem?.address?.city || '',
      state: addressItem?.address?.state || '',
      zip: addressItem?.address?.zip || '',
      name: addressItem?.address?.name || '',
      latitude: addressItem?.address?.latitude ? Number(addressItem?.address?.latitude) : null,
      longitude: addressItem?.address?.longitude ? Number(addressItem?.address?.longitude) : null,
      googlePlaceId: addressItem?.address?.googlePlaceId,
    };
  },
  {
    immediate: true,
  },
);

watch(
  endAddress,
  (value: any, oldValue: any) => {
    const isAddressChanged = util.isAddressChanged(value, oldValue);

    const { latitude, longitude } = value;

    setMarkerPosition(Number(latitude), Number(longitude), 1, false, isAddressChanged);
  },
  {
    immediate: true,
  },
);

watch(
  availableRideTypes,
  () => {
    rideScheduler.value.rideType = getAvailableRideType({
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    });
  },
  {
    immediate: true,
  },
);

watch(rideScheduler, (rs) => {
  rideScheduler.value.scheduleKind = rs.rideMultiple ? 'multi' : 'single';
});

watch(
  () => rideScheduler.value.scheduleKind,
  (v) => {
    switch (v) {
      case 'multi':
        return (rideScheduler.value.rideMultiple = true);
      case 'willcall':
          rideScheduler.value.rideMultiple = false;
          rideScheduler.value.isWillCall = true;
          // just set to mid day
          // it doesn't matter in willcall
          // only need to make sure timezone shifts won't move it to a different date
          rideScheduler.value.pickupTime = Moment().hour(12).toDate();
          return;
      default:
        return (rideScheduler.value.rideMultiple = false);
    }
  },
);

watch(
  () => rideScheduler.value.isExpandDriverSearch,
  (value: boolean) => {
    rideScheduler.value.overrideBadDriver = value;
  },
);

watch(
  () => rideScheduler.value.isExpandVehicleSearch,
  (value: boolean) => {
    rideScheduler.value.overrideBigCar = value;
    rideScheduler.value.overrideSmallCar = value;
    rideScheduler.value.overrideBannedCar = value;
  },
);

watch(
  () => rideScheduler.value.pickupTime,
  (value: string) => {
    if (value) {
      const currentTime = util.formatDateTime(value, 'HH:mm') as string;
      status.isShowTimeWarningMessage = currentTime.localeCompare('00:00') >= 0 && currentTime.localeCompare('05:00') <= 0;
    }
  },
);

onMounted(() => {
  util.moveToTop();

  scheduledRideId.value = route.query.scheduledRideId ? Number(route.query.scheduledRideId as string) : 0;

  if (scheduledRideId.value) {
    getRideScheduler();
  } else if (duplicatedScheduledRide.value) {
    const data = rideUtility.getRideSchedulerData(duplicatedScheduledRide.value);
    startAddress.value = _.cloneDeep(data.startAddress);
    endAddress.value = _.cloneDeep(data.endAddress);
    rideScheduler.value = _.omit(data, ['startAddress', 'endAddress']);

    setDuplicatedScheduledRide(null);
    rideScheduler.value.pickupDate = null;
    rideScheduler.value.pickupTime = null;
  } else {
    const startDate = route.query.startDate ? Moment(route.query.startDate as string).toDate() : null;
    rideScheduler.value.pickupDate = startDate;
  }
});
</script>

<style lang="scss">
.family-scheduled-ride.page-content {
  .el-date-editor {
    width: 200px !important;
  }
}
</style>
